import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { FadeUp, ParallaxImage, Eyebrow, MaskedLines } from "@/components/Reveal";
import { DagazMark } from "@/components/Logo";
import { IMAGES, SITE } from "@/data/content";

export default function About() {
    return (
        <>
            <Seo
                title="About — European Design, Built Locally | Dagaz Development"
                description="Dagaz Development is an Oregon design-and-build company founded by Miro Damonson. European design discipline, delivered with licensed local builders across the US and Canada."
                path="/about"
                image={IMAGES.pavilions}
                jsonLd={[
                    orgSchema,
                    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
                    {
                        "@context": "https://schema.org",
                        "@type": "AboutPage",
                        name: "About Dagaz Development",
                        mainEntity: {
                            "@type": "Person",
                            name: "Miro Damonson",
                            jobTitle: "Founder",
                            worksFor: { "@type": "Organization", name: SITE.name },
                        },
                    },
                ]}
            />
            <PageHeader
                eyebrow="About — The Company"
                titleLines={["European design.", "Built by local hands."]}
                answer={SITE.entity}
                crumbs={[{ name: "About" }]}
            />

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-36 bg-chalk grid lg:grid-cols-12 gap-14" data-testid="founder-section">
                <div className="lg:col-span-5">
                    <FadeUp>
                        <ParallaxImage
                            src={IMAGES.architect}
                            alt="Design drawings being drafted at Dagaz Development"
                            className="h-[420px] md:h-[560px]"
                            speed={7}
                        />
                        <p className="mt-4 font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate">
                            Miro Damonson — Founder
                        </p>
                    </FadeUp>
                </div>
                <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
                    <Eyebrow className="mb-8">The Founder</Eyebrow>
                    <MaskedLines
                        lines={["Fewer, better", "buildings."]}
                        inView
                        className="font-serif font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-graphite"
                    />
                    <FadeUp delay={0.2} className="mt-8 space-y-6 font-sans text-base leading-relaxed text-graphite/80">
                        <p>
                            Miro Damonson trained in art and design in Europe, then spent years on the delivery side — managing development projects with DMELL in Slovakia, where he learned that beautiful drawings mean nothing without disciplined budgets and honest schedules.
                        </p>
                        <p>
                            He moved to Oregon for the same reason the work looks the way it does: forest, stone, weather and light. Dagaz Development was founded to bring those two halves together — European design discipline, delivered with licensed local builders who know their ground.
                        </p>
                        <p>
                            The rule has not changed since the first project: build fewer things, build them properly, and be reachable when it matters.
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section className="bg-forest bg-noise px-6 md:px-12 lg:px-16 py-28 md:py-40" data-testid="rune-section">
                <div className="grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-3">
                        <FadeUp>
                            <DagazMark className="w-24 h-24 text-chalk" />
                        </FadeUp>
                    </div>
                    <div className="lg:col-span-8 lg:col-start-5">
                        <Eyebrow dark className="mb-8">Why Dagaz</Eyebrow>
                        <MaskedLines
                            lines={["The rune of dawn", "and new beginnings."]}
                            inView
                            className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-chalk"
                        />
                        <FadeUp delay={0.25} className="mt-8 max-w-2xl">
                            <p className="font-sans text-base leading-relaxed text-fog">
                                Every project is a beginning — a first home, a rebuild after loss, a venture that needs a roof. The name is a promise about how we approach that threshold: quietly, precisely, and with the long life of the building in mind.
                            </p>
                        </FadeUp>
                        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-chalk/15 pt-10">
                            {["Form", "Material", "Precision", "Place"].map((v, i) => (
                                <FadeUp key={v} delay={0.1 * i}>
                                    <p className="font-serif font-light text-2xl text-chalk">{v}</p>
                                    <p className="mt-2 font-sans text-[10px] tracking-[0.24em] uppercase text-slate">{["Considered", "Honest", "Disciplined", "Rooted"][i]}</p>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="px-6 md:px-12 lg:px-16 py-24 md:py-36 bg-chalk" data-testid="coverage-section">
                <Eyebrow className="mb-8">Where We Work</Eyebrow>
                <MaskedLines
                    lines={["One office in Oregon.", "Sixty-three jurisdictions."]}
                    inView
                    className="font-serif font-light text-4xl md:text-6xl leading-[1.02] tracking-tight text-graphite"
                />
                <div className="mt-14 grid md:grid-cols-2 gap-10 max-w-4xl">
                    <FadeUp className="border-l-2 border-gold pl-6">
                        <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate mb-3">United States</p>
                        <p className="font-sans text-base leading-relaxed text-graphite/80">
                            All 50 states — from the Pacific Northwest to New England, the Rockies to the Gulf. Delivery with licensed builders in each state, managed from Oregon.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.15} className="border-l-2 border-gold pl-6">
                        <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate mb-3">Canada</p>
                        <p className="font-sans text-base leading-relaxed text-graphite/80">
                            Every province and territory — British Columbia to Newfoundland, the Prairies to the North. The same model, the same discipline, in both languages of building code.
                        </p>
                    </FadeUp>
                </div>
                <FadeUp delay={0.2} className="mt-12 max-w-4xl">
                    <p className="font-sans text-sm leading-relaxed text-slate">
                        Past projects delivered across Europe — Slovakia, Germany and Austria — now joined by work underway across the United States and Canada.
                    </p>
                </FadeUp>
            </section>
        </>
    );
}
